// Event ticket QR credential.
//
// Wire format (same envelope as every BRE credential):
//   base64url(JSON payload) "." base64url(HMAC-SHA256(key, payloadBytes))
//
// The key is derived per performance from EVENT_TICKET_SIGNING_SECRET so an
// offline scanner bundle only ever carries the key for the performance it is
// admitting (docs/ticket-lifecycle.md#credentials). QR authenticity proves the
// string was minted by Ticketing; the ticket row decides validity.
import { z } from 'zod';
import { Id } from './primitives';

export const TicketCredentialPayload = z.strictObject({
  v: z.literal(1),
  tid: Id,
  pid: Id,
  cv: z.int().min(1),
});
export type TicketCredentialPayload = z.infer<typeof TicketCredentialPayload>;

/** Manual-entry short code: no 0/O/1/I/L, same alphabet as POS credentials. */
export const SHORT_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const SHORT_CODE_LENGTH = 10;
export const ShortCode = z
  .string()
  .transform((value) => value.trim().toUpperCase())
  .pipe(z.string().regex(/^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{10}$/));

/** A scanned string with a '.' is a signed credential; otherwise a short code. */
export function classifyScanInput(raw: string): 'CREDENTIAL' | 'SHORT_CODE' {
  return raw.includes('.') ? 'CREDENTIAL' : 'SHORT_CODE';
}

// Season pass QR credential (D-373).
//
// The same envelope as a ticket's and the same master secret, but its own key
// purpose: the key is derived per SEASON ("season-pass:v1:" + season id), not
// per performance, because one pass admits to every covered game. The payload
// names its season so a verifier picks the key without a lookup, which is what
// lets an offline scanner verify a pass holding only the key for tonight's
// season. `typ` keeps a pass payload from ever parsing as a ticket's
// (TicketCredentialPayload is strict), and the other way round.
export const SeasonPassCredentialPayload = z.strictObject({
  v: z.literal(1),
  typ: z.literal('SP'),
  sp: Id,
  sid: Id,
  cv: z.int().min(1),
});
export type SeasonPassCredentialPayload = z.infer<typeof SeasonPassCredentialPayload>;
